import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LineChart, Menu, X, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const NAV_LINKS = [
  { to: "/dashboard", label: "Scanner", testid: "nav-dashboard" },
  { to: "/demo", label: "My Universe", testid: "nav-demo" },
  { to: "/portfolio", label: "My Stocks", testid: "nav-portfolio" },
  { to: "/horizons", label: "Horizons", testid: "nav-horizons" },
  { to: "/metals", label: "Metals", testid: "nav-metals" },
  { to: "/land", label: "Land", testid: "nav-land" },
  { to: "/compare", label: "Compare", testid: "nav-compare" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b transition-shadow duration-300 ${
        scrolled ? "border-line shadow-md shadow-stone_brand-dark/5" : "border-line/50"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <div className="w-8 h-8 bg-moss flex items-center justify-center rounded-sm">
            <LineChart className="w-4 h-4 text-bone-50" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-xl font-semibold text-stone_brand-dark tracking-tight">
              Fairvest
            </div>
            <div className="overline text-[0.6rem] text-stone_brand-light">
              Equity Research
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        {user && (
          <nav className="hidden md:flex items-center gap-4 lg:gap-5" data-testid="main-nav">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === l.to
                    ? "text-moss"
                    : "text-stone_brand hover:text-stone_brand-dark"
                }`}
                data-testid={l.testid}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              <div className="hidden sm:flex flex-col items-end">
                <div className="text-xs text-stone_brand-light font-mono uppercase tracking-wider">
                  Logged in
                </div>
                <div className="text-sm text-stone_brand-dark font-medium" data-testid="navbar-user-email">
                  {user.email}
                </div>
              </div>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="hidden md:inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-moss/40 bg-moss-bg/40 text-moss text-xs font-mono uppercase tracking-wider hover:bg-moss hover:text-bone-50 transition"
                  data-testid="navbar-admin-link"
                >
                  <Shield className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="hidden md:inline-flex rounded-sm border-line text-stone_brand hover:bg-bone-100 hover:text-stone_brand-dark"
                data-testid="navbar-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
              {/* Mobile hamburger */}
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="md:hidden p-2 rounded-sm border border-line text-stone_brand-dark hover:bg-bone-100 transition-colors"
                data-testid="navbar-mobile-menu-btn"
                aria-label="Menu"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <Link to="/login">
              <Button
                className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50"
                data-testid="navbar-login-btn"
              >
                Sign In
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Mobile menu panel */}
      {user && menuOpen && (
        <div
          className="md:hidden border-t border-line bg-white/95 backdrop-blur-xl"
          data-testid="navbar-mobile-menu"
        >
          <nav className="px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  location.pathname === l.to
                    ? "bg-moss-bg text-moss"
                    : "text-stone_brand-dark hover:bg-bone-100"
                }`}
                data-testid={`mobile-${l.testid}`}
              >
                {l.label}
              </Link>
            ))}
            {user.role === "admin" && (
              <Link
                to="/admin"
                className={`px-3 py-2.5 rounded-sm text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  location.pathname === "/admin"
                    ? "bg-moss text-bone-50"
                    : "text-moss hover:bg-moss-bg"
                }`}
                data-testid="mobile-nav-admin"
              >
                <Shield className="w-4 h-4" /> Admin
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-medium text-terracotta hover:bg-terracotta-bg transition-colors text-left"
              data-testid="mobile-nav-logout"
            >
              <LogOut className="w-4 h-4" /> Log out · {user.email}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
